// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * AddSubAccountModal — Clinic Team Master + Sub Account (vetpo wiring).
 *
 * A Master Account creates a per-vet Sub Account. POSTs to the Phase-2a
 * backend endpoint POST /api/users/create-sub-account (see ada-core
 * src/collections/Users/createSubAccount.ts). The sub receives an activation
 * email unless "send invite now" is unticked. Mirrors the fetch + notify
 * pattern of src/owners/RegisterOwner.tsx.
 */
import { Close as CloseIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders, EMAIL_RE } from '../utils/api'

interface AddSubAccountModalProps {
  open: boolean
  /** Called after the modal closes; pass `created` so the list can refresh. */
  onClose: (created: boolean) => void
}

interface FormState {
  firstName: string
  lastName: string
  email: string
  title: string
  phone: string
  allowAlerts: boolean
  sendInvite: boolean
}

const emptyForm: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  title: '',
  phone: '',
  allowAlerts: true,
  sendInvite: true,
}

const AddSubAccountModal: React.FC<AddSubAccountModalProps> = ({ open, onClose }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [form, setForm] = useState<FormState>(emptyForm)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  // Set when create-sub-account 409s: the email already has an account, so we
  // offer the invite-and-accept path (link the EXISTING account) instead of a
  // dead end. See ada-core src/collections/Users/clinicTeamInvite.ts.
  const [linkableEmail, setLinkableEmail] = useState('')
  const [inviting, setInviting] = useState(false)

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }))

  const reset = () => {
    setForm(emptyForm)
    setError('')
    setSubmitting(false)
    setLinkableEmail('')
    setInviting(false)
  }

  const handleClose = (created: boolean) => {
    reset()
    onClose(created)
  }

  const handleSubmit = async () => {
    const firstName = form.firstName.trim()
    const lastName = form.lastName.trim()
    const email = form.email.trim().toLowerCase()

    if (!firstName || !lastName) {
      setError(translate('pos.clinic_team.error_name_required'))
      return
    }
    if (!EMAIL_RE.test(email)) {
      setError(translate('pos.clinic_team.error_email_invalid'))
      return
    }

    setError('')
    setSubmitting(true)
    try {
      const response = await deadlineFetch('/api/users/create-sub-account', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          ...(form.title.trim() ? { title: form.title.trim() } : {}),
          ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
          allowAlerts: form.allowAlerts,
          sendInvite: form.sendInvite,
        }),
      })
      const data = await response.json().catch(() => ({}))

      if (response.status === 201 && data?.success) {
        notify(
          form.sendInvite ? 'pos.clinic_team.created_invited' : 'pos.clinic_team.created_no_invite',
          { type: 'success', messageArgs: { email } },
        )
        handleClose(true)
        return
      }
      if (response.status === 409) {
        setError(translate('pos.clinic_team.error_email_exists'))
        // Offer the link-existing-account path (invite-and-accept).
        setLinkableEmail(email)
      } else {
        setError(data?.errors?.[0]?.message || translate('pos.clinic_team.error_generic'))
      }
    } catch (_e) {
      notify('pos.clinic_team.error_network', { type: 'error' })
      setError(translate('pos.clinic_team.error_network'))
    } finally {
      setSubmitting(false)
    }
  }

  // Invite the EXISTING account at this email to join the team. Consent-based:
  // nothing changes until the account holder logs in and accepts the emailed
  // invitation, so the master never silently gains access to anyone's data.
  const handleInvite = async () => {
    setInviting(true)
    try {
      const response = await deadlineFetch('/api/users/invite-existing-sub', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ email: linkableEmail }),
      })
      const data = await response.json().catch(() => ({}))
      if (response.status === 200 && data?.success) {
        notify('pos.clinic_team.invite_existing_sent', {
          type: 'success',
          messageArgs: { email: linkableEmail },
        })
        handleClose(false)
        return
      }
      // Server messages here are actionable ("already belongs to a clinic
      // team", "has its own team", "pet-owner account with active pets") —
      // surface them verbatim.
      setError(data?.errors?.[0]?.message || translate('pos.clinic_team.error_generic'))
      setLinkableEmail('')
    } catch (_e) {
      setError(translate('pos.clinic_team.error_network'))
    } finally {
      setInviting(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => handleClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {translate('pos.clinic_team.add_title')}
        <IconButton
          aria-label={translate('pos.common.close')}
          onClick={() => handleClose(false)}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('pos.clinic_team.add_description')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 0, sm: 2 },
          }}
        >
          <TextField
            label={translate('pos.clinic_team.first_name')}
            value={form.firstName}
            onChange={(e) => set({ firstName: e.target.value })}
            fullWidth
            required
            margin="dense"
          />
          <TextField
            label={translate('pos.clinic_team.last_name')}
            value={form.lastName}
            onChange={(e) => set({ lastName: e.target.value })}
            fullWidth
            required
            margin="dense"
          />
        </Box>
        <TextField
          label={translate('pos.clinic_team.email')}
          type="email"
          value={form.email}
          onChange={(e) => set({ email: e.target.value })}
          fullWidth
          required
          margin="dense"
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 0, sm: 2 },
          }}
        >
          <TextField
            label={translate('pos.clinic_team.professional_title')}
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label={translate('pos.clinic_team.phone')}
            value={form.phone}
            onChange={(e) => set({ phone: e.target.value })}
            fullWidth
            margin="dense"
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={form.allowAlerts}
              onChange={(e) => set({ allowAlerts: e.target.checked })}
            />
          }
          label={translate('pos.clinic_team.allow_alerts')}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.sendInvite}
              onChange={(e) => set({ sendInvite: e.target.checked })}
            />
          }
          label={translate('pos.clinic_team.send_invite')}
        />
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
        {linkableEmail && (
          <Box
            data-testid="invite-existing-block"
            sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              {translate('pos.clinic_team.invite_existing_prompt', { email: linkableEmail })}
            </Typography>
            <Button variant="outlined" size="small" onClick={handleInvite} disabled={inviting}>
              {translate('pos.clinic_team.invite_existing_button')}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(false)} disabled={submitting}>
          {translate('ra.action.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {translate('pos.clinic_team.add_submit')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddSubAccountModal
